import React, { useEffect, useId, useRef } from "react";
import "./Segmented.scss";

// Fontaine M16 — Segmented control: exclusive inline choice (All / Active / Reserved / Sold, grid / list).
// Replaces ad-hoc pill rows. role="radiogroup" with roving focus and arrow keys; scrolls on phones with edge fade.

/**
 * @param {{value: string, label: ReactNode, icon?: ReactNode, count?: number, disabled?: boolean}[]} options
 * @param {string} value
 * @param {(value: string) => void} onChange
 * @param {"sm"|"md"|"lg"} [size="md"]     lg matches the lg field / button row
 * @param {string} label                      accessible name of the group (required)
 * @param {boolean} [fullWidth]               segments share the width equally
 * @param {boolean} [iconOnly]                show the glyph only; the label stays as the accessible name
 */
export default function Segmented({ options = [], value, onChange, size = "md", label, "aria-label": ariaLabel, fullWidth = false, iconOnly = false, className = "", ...rest }) {
  const id = useId();
  const listRef = useRef(null);
  const name = ariaLabel || label;
  if (import.meta.env?.DEV && !name && !rest["aria-labelledby"]) {
    console.error("Segmented: an accessible name is required — pass label=\"…\".");
  }

  // Keep the selected segment in view on phones (horizontal scroll).
  useEffect(() => {
    const el = listRef.current?.querySelector('[aria-checked="true"]');
    if (el && listRef.current && listRef.current.scrollWidth > listRef.current.clientWidth) {
      el.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [value]);

  const enabled = options.filter((o) => !o.disabled);
  const move = (delta) => {
    if (!enabled.length) return;
    const i = Math.max(0, enabled.findIndex((o) => o.value === value));
    const next = enabled[(i + delta + enabled.length) % enabled.length];
    onChange?.(next.value);
    listRef.current?.querySelector(`[data-value="${next.value}"]`)?.focus();
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.key === "Home") { e.preventDefault(); onChange?.(enabled[0]?.value); }
    else if (e.key === "End") { e.preventDefault(); onChange?.(enabled[enabled.length - 1]?.value); }
  };

  const classes = ["ft-seg", `ft-seg--${["sm", "lg"].includes(size) ? size : "md"}`, fullWidth ? "ft-seg--full" : "", iconOnly ? "ft-seg--icon-only" : "", className].filter(Boolean).join(" ");

  return (
    <div className={classes} role="radiogroup" aria-label={name} ref={listRef} onKeyDown={onKeyDown} {...rest}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (value == null && o === enabled[0]) ? 0 : -1}
            data-value={o.value}
            id={`${id}-${o.value}`}
            className={`ft-seg__item${selected ? " is-selected" : ""}`}
            disabled={o.disabled}
            onClick={() => onChange?.(o.value)}
          >
            {o.icon ? <span className="ft-seg__icon" aria-hidden="true">{o.icon}</span> : null}
            <span className="ft-seg__label">{o.label}</span>
            {typeof o.count === "number" ? <span className="ft-seg__count">{o.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
